import React from "react";
import {
  Box,
  Typography,
  Button,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
} from "@mui/material";
import { Person as PersonIcon } from "@mui/icons-material";
import { formatDate } from "../../../../utils/dateUtils";

const PreviewRow = ({ icon: Icon, label, reason, validUntil }) => {
  const theme = useTheme();
  const validUntilLabel =
    validUntil === undefined
      ? null
      : validUntil === null
        ? "Permanent"
        : (() => {
            const d = formatDate(validUntil);
            return d ? `Until ${d}` : "Permanent";
          })();

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        gap: 2,
        py: 1.5,
        borderBottom: "1px solid rgba(0, 0, 0, 0.08)",
        "&:last-child": {
          borderBottom: "none",
        },
      }}
    >
      <Icon
        sx={{
          color: theme.palette?.text?.secondary || "#666",
          fontSize: 20,
          flexShrink: 0,
          mt: 0.25,
        }}
      />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            justifyContent: "space-between",
          }}
        >
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              color: theme.palette?.text?.primary,
              wordBreak: "break-all",
              fontSize: "0.875rem",
              flex: 1,
              minWidth: 0,
            }}
          >
            {label || "—"}
          </Typography>
          {validUntilLabel && (
            <Chip
              label={validUntilLabel}
              size="small"
              sx={{
                flexShrink: 0,
                bgcolor: "rgba(26,86,219,0.08)",
                color: "primary.main",
                fontWeight: 500,
                fontSize: "0.75rem",
                height: 24,
                borderRadius: "6px",
                px: 1,
              }}
            />
          )}
        </Box>
        {reason && (
          <Typography
            variant="caption"
            sx={{
              display: "-webkit-box",
              mt: 0.5,
              color: theme.palette?.text?.secondary || "#666",
              fontSize: "0.75rem",
              lineHeight: 1.4,
              overflow: "hidden",
              textOverflow: "ellipsis",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              wordBreak: "break-word",
            }}
          >
            {reason}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

const BulkDeleteExceptionDialog = ({
  open,
  onCancel,
  onConfirm,
  loading,
  count,
  previewItems = [],
  entityLabel = "exception",
  entityIcon = PersonIcon,
}) => {
  const theme = useTheme();
  const plural = count > 1 ? "s" : "";

  return (
    <Dialog
      open={open}
      onClose={() => !loading && onCancel?.()}
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
          minWidth: 600,
          maxWidth: 700,
        },
      }}
    >
      <DialogTitle
        sx={{
          fontWeight: 600,
          fontSize: "1.25rem",
          color: theme.customColors.text.strong,
          background: theme.customColors.background.cardHeader,
          borderBottom: `1px solid ${theme.customColors.border.main}`,
          pb: 2,
          pt: 2,
          px: 3,
        }}
      >
        Delete {count} {entityLabel}
        {plural}
      </DialogTitle>

      <DialogContent
        sx={{
          px: 3,
          pt: 3,
          pb: 0,
          "&.MuiDialogContent-root": {
            paddingTop: 3,
          },
        }}
      >
        <Alert
          severity="error"
          sx={{ mb: 2, fontSize: "0.875rem", py: 0.5 }}
        >
          You're about to delete {count} {entityLabel}
          {plural}. This action cannot be undone.
        </Alert>

        {previewItems.length > 0 && (
          <Box
            sx={{
              maxHeight: "45vh",
              overflow: "auto",
              pr: 0.5,
              border: `1px solid ${theme.customColors.border.subtle}`,
              borderRadius: 1,
              px: 1.5,
            }}
          >
            {previewItems.map((item, idx) => {
              const normalized =
                typeof item === "string" ? { label: item } : item || {};
              return (
                <PreviewRow
                  key={`${normalized.label}-${idx}`}
                  icon={entityIcon}
                  label={normalized.label}
                  reason={normalized.reason}
                  validUntil={normalized.validUntil}
                />
              );
            })}
          </Box>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 1,
          px: 3,
          pb: 2,
          pt: 2,
          borderTop: `1px solid ${theme.customColors.border.subtle}`,
          mt: 1,
        }}
      >
        <Button
          onClick={onCancel}
          disabled={loading}
          variant="outlined"
          size="small"
          sx={{
            fontWeight: 500,
            fontSize: "0.8125rem",
            px: 2,
            py: 0.75,
            borderRadius: 1,
            borderColor: theme.customColors.border.main,
            color: theme.customColors.text.strong,
            "&:hover": {
              borderColor: theme.customColors.border.strong,
              backgroundColor: theme.customColors.background.hover,
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          disabled={loading || count === 0}
          size="small"
          sx={{
            fontWeight: 500,
            fontSize: "0.8125rem",
            px: 2,
            py: 0.75,
            borderRadius: 1,
            bgcolor: theme.customColors.button.primary,
            color: theme.customColors.button.text,
            "&:hover": {
              bgcolor: theme.palette._blue.dark,
            },
            "&:disabled": {
              bgcolor: theme.customColors.background.light,
              color: theme.customColors.text.disabled,
            },
          }}
        >
          {loading ? "Deleting..." : `Delete`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BulkDeleteExceptionDialog;
